import { Link, useNavigate, useParams } from "react-router-dom"
import { app, translate } from "../app"
import { languages } from "../locale"
import { ReadTime } from "./readingtime"
import { translations } from "../translations"
import { useState, useEffect, useCallback, useMemo } from "react"
import Fuse from "fuse.js"
import { Footer } from "./footer"
import { Helmet } from "react-helmet"
import { EmptyImage, RightOutlined } from "./icons"
import { generateHreflangTag, getLocaleByZendesk } from "../helpers"

import { Breadcrumb } from "./breadcrumb"
import { Pagination } from "./pagination"
import { Card } from "./card"
import { Tag } from "./tag"
import { Search } from "./searchinput"
import { Select } from "./select"


const allOption = {
  description: {}, id: 0, name: {
    "en-US": "All",
    "ar-SA": "الكل",
    "fr-FR": "Tous",
    "es-ES": "Todo",
    "so-SO": "Dhammaan",
    "ur-UR": "سب",
    "ht": "Tout",
    "fr-HT": "Tout",
    "bn": "সব",
    "my": "အားလုံး",
    "cs": "Vše",
    "fa-AF": "همه",
    "fa-FA": "همه",
    "de-DE": "Alle",
    "el": "Όλα",
    "ha-HA": "Duka",
    "hu-HU": "Összes",
    "it-IT": "Tutti",
    "KAU": "Duka",
    "ki-KI": "Yote",
    "ln-LN": "Nyonso",
    "ps-PS": "ټول",
    "ru-RU": "Все",
    "sw-SW": "Yote",
    "th": "ทั้งหมด",
    "uk-UK": "Всі"
  }
}

const colors = ['#FDF1B1', '#F9D79E', '#ABDFBF', '#C0E1E0', '#B1C3FB', '#CDA9E9']

export function Categories() {

  const navigate = useNavigate()
  const { categoryid, sectionid, locale } = useParams()
  const languageSelected = locale ? getLocaleByZendesk(locale.toLowerCase()) : (localStorage.getItem('preferredLanguage') ? localStorage.getItem('preferredLanguage') : app.locale)
  const selectedLocale = languages[languageSelected]?.zendesk || languageSelected
  app.locale = languageSelected
  const isRTL = languages[languageSelected]?.rtl

  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredArticles, setFilteredArticles] = useState<ZendeskArticle[]>([])
  const [paginatedArticles, setPaginatedArticles] = useState<ZendeskArticle[]>([])
  const [initialized, setInitialized] = useState(false)
  const [order, setOrder] = useState('')
  const pageSize = 12
  const c = app.data.zendesk.categories[categoryid] as ZendeskCategory | undefined
  const s = app.data.zendesk.sections[sectionid] as ZendeskSection | undefined

  const onSelect = useCallback((data: { key: string; label: string; value: string }) => {
    setOrder(data.value)
  }, [])

  useEffect(() => {
    if (!initialized && Object.keys(app.data.zendesk.categories || {}).length > 0) {
      setInitialized(true)
    }
  }, [app.data.zendesk.categories])

  const categories: ZendeskCategory[] = useMemo(() => {
    const cat = Object.values(app.data.zendesk.categories || {})
    cat.unshift(allOption)
    return cat
  }, [initialized, categoryid])

  let categorySections: ZendeskSection[] = useMemo(() => {
    if (c) {
      const sections = Object.values(c.sections)
      sections.unshift(allOption)
      return sections
    }
    return [allOption]
  }, [c, allOption])

  const a = useMemo(() => Object.values(app.data.zendesk.articles), [initialized])

  const filterArticles = useCallback(() => {
    if (c) {
      if (s) {
        return a.filter(article => article.section === s.id && article?.name[languageSelected])
      } else {
        return a.filter(article => article.category === c.id && article?.name[languageSelected])
      }
    } else {
      return a
    }
  }, [a, c, s])

  const stripHtmlTags = (html) => {
    const regex = /<[^>]*>|&[^;]+;|<img\s+.*?>|<span\s+style="[^"]*">.*?<\/span>|&[A-Za-z]+;/g
    return html.replace(regex, '')
  }

  const flattenZendeskData = (data: ZendeskArticle[]) => {
    return data.flatMap(item => {
      const nameEntries = Object.entries(item.name).map(([lang, text]) => ({
        id: item.id,
        section: item.section,
        category: item.category,
        lang,
        type: 'name',
        text,
      }))

      const descriptionEntries = Object.entries(item.description).map(([lang, text]) => ({
        id: item.id,
        section: item.section,
        category: item.category,
        lang,
        type: 'description',
        text,
      }))

      return [...nameEntries, ...descriptionEntries]
    })
  }

  const scrollUp = () => {
    setTimeout(() => {
      const element = document.getElementById("search-categories")
      if (element) {
        element.scrollIntoView({ behavior: "smooth" })
      }
    }, 300)
  }

  const performSearch = useCallback((articles) => {
    const options = {
      includeMatches: true,
      keys: ['text'],
      shouldSort: true,
      customSortFn: (a, b) => a.score - b.score,
    }
    const articleSearch = new Fuse(flattenZendeskData(articles), options)
    const articleSearchResult = articleSearch.search(searchTerm || '').map(result => result.item)
    const uniqueArticlesIds = [...new Set(articleSearchResult.map(result => result.id))]
    return uniqueArticlesIds.map(id => articles.find(item => item.id === id))
  }, [searchTerm])

  const sortByUpdatedAt = (arr: ZendeskArticle[], order: string) => {
    if (order === '') return arr
    return [...arr].sort((a, b) => {
      const dateA = new Date(a?.updated_at);
      const dateB = new Date(b?.updated_at);

      const comparison = dateB?.getTime() - dateA?.getTime();

      return order === 'oldest' ? -comparison : comparison;
    });
  }

  useEffect(() => {
    const articles = filterArticles()
    if (searchTerm) {
      const searchResults = performSearch(articles)
      setFilteredArticles(sortByUpdatedAt(searchResults, order))
    } else {
      setFilteredArticles(sortByUpdatedAt(articles, order))
    }
    setCurrentPage(1)
  }, [categoryid, sectionid, searchTerm, filterArticles, performSearch, selectedLocale, order])

  useEffect(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    setPaginatedArticles(filteredArticles.slice(startIndex, endIndex))
  }, [filteredArticles, currentPage])

  useEffect(() => {
    if (Object.values(app.data.zendesk.categories).length) {
      setInitialized(true)
    }
  }, [app.data.zendesk.categories])



  const metadata = {
    title: sectionid && s ? translate(s.name) : (categoryid && c ? translate(c.name) : translate(translations.resourceCenter)),
    description: sectionid && s ? translate(s.name) : (categoryid && c ? translate(c.name) : translate(translations.resourceCenter)),
    url: `${window.location.origin}/${selectedLocale.toLowerCase()}/categories/${categoryid || ""}/${sectionid || ""}`,
  }

  const hreflangTags = s?.name ? generateHreflangTag("categories", s?.name, `${c.id}/${sectionid}`) : (c?.name ? generateHreflangTag("categories", c?.name, categoryid) : generateHreflangTag("categories"))


  let totalItems = filteredArticles.length


  return <div className={`flex overflow-y-scroll flex-col ${isRTL ? 'rtl' : ''}`}>

    {/* Metadata */}
    <Helmet>
      <title>{metadata.title}</title>
      <meta name="description" content={metadata.description} />
      {hreflangTags?.map((tag, index) => (
        <link key={index} rel="alternate" hrefLang={tag.hreflang} href={tag.href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${process.env.NEXT_PUBLIC_SITE_URL}/categories/${sectionid ? categoryid + '/' + sectionid : categoryid ? categoryid : ''}`} />
      <meta property="og:title" content={metadata.title} />
      <meta property="og:description" content={metadata.description} />
      <meta property="og:url" content={metadata.url} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={metadata.title} />
      <meta name="twitter:description" content={metadata.description} />
    </Helmet>

    <div className="py-16 w-full flex justify-center text-black bg-white h-auto">

      <div className="sm:w-full px-4 md:w-4/5 h-fit">
        <Breadcrumb isRtl={isRTL} items={[
          { link: `/${selectedLocale.toLowerCase()}`, title: translate(translations.home) },
          { title: translate(translations.resourceCenter) }]}
        />
        <div className="bg-[#F7F7F7] px-4 pb-4 pt-[1px] mb-4">
          <h1 className={`text-4xl font-medium leading-normal ${isRTL ? 'text-right' : 'text-left'}`} style={{ direction: isRTL ? "rtl" : "ltr" }}> {translate(translations.category)}</h1>
          <div className="flex gap-4 flex-wrap">
            {categories.map((category, index) => (
              category.name[languageSelected] && (<button
                key={category.id}
                aria-pressed={category.id === +categoryid || (category.id === 0 && !categoryid)}
                className={`category-filter-button text-sm font-inter ${category.id === +categoryid || (category.id === 0 && !categoryid) ? 'active' : ''}`}
                onClick={() => category.id === 0 ? navigate(`/${selectedLocale.toLowerCase()}/categories/`) : navigate(`/${selectedLocale.toLowerCase()}/categories/${category.id}/`)}
                style={{ backgroundColor: colors[index % colors.length], border: `1px solid ${colors[index % colors.length]}` }}
              >
                {translate(category.name)}
              </button>)
            ))}
          </div>
        </div>
        <div className="bg-[#F7F7F7] px-4 pb-4 pt-[1px]">
          <h1 className={`text-3xlleading-normal font-normal normal-case ${isRTL ? 'text-right' : 'text-left'}`} style={{ direction: isRTL ? "rtl" : "ltr" }} >{translate(translations.topic)}</h1>
          <div className="flex gap-4 flex-wrap">
            {categorySections.map((section, index) => (
              section.name[languageSelected] && (<button
                key={section.id}
                aria-pressed={section.id === +sectionid || (section.id === 0 && !sectionid)}
                className={`category-filter-button text-sm font-inter ${section.id === +sectionid || (section.id === 0 && !sectionid) ? 'active' : ''}`}
                onClick={() => !c ? navigate(`/${selectedLocale.toLowerCase()}/categories/`) : section.id === 0 ? navigate(`/${selectedLocale.toLowerCase()}/categories/${c.id}/`) : navigate(`/${selectedLocale.toLowerCase()}/categories/${c.id}/${section.id}/`)}
                style={{ backgroundColor: colors[index % colors.length], border: `1px solid ${colors[index % colors.length]}` }}
              >
                {translate(section.name)}
              </button>)
            ))}
          </div>
        </div>

        <div className="w-full gap-5 flex mt-8 flex-col md:flex-row" id="search-categories">
          <div className="w-full">
            <Search isRtl={isRTL} onSearch={(value) => setSearchTerm(value)} />
          </div>
          <Select
            options={[{ label: 'Oldest', value: 'oldest' }, { label: 'Newest', value: 'newest' }]}
            placeholder={translate(translations.filterByNewOrOld)}
            onSelect={onSelect}
            onClear={() => setOrder('')}
            isRtl={isRTL}
          />
        </div>

        <div className={`${paginatedArticles.length ? 'grid lg:grid-cols-4 md:grid-cols-3 gap-6 mt-10' : ''}`}>
          {paginatedArticles?.length ? paginatedArticles.map(article => (
            <Link to={`/${selectedLocale.toLowerCase()}/articles/${article.id}`} key={article.id} className="no-underline">
              <Card className={`articles-card ${isRTL ? 'rtl-card' : ''}`} style={{ height: '100%' }}>
                <div className="flex justify-between">
                  <Tag color="#87d068" className="max-w-[65%] break-words whitespace-normal bg-lime-50 text-black">{translate(categories.find(category => category.id === article.category)?.name)}</Tag>
                  <ReadTime content={translate(article.description)} />
                </div>
                <h1 className="text-base font-bold">{translate(article.name)}</h1>
                <p className="text-sm font-light text-gray-500">{`${stripHtmlTags(translate(article.description))?.slice(0, 100)}...`}</p>
                <p className="mt-4 text-sm">{new Date(article.updated_at).toLocaleDateString('en-GB')}</p>
                <span className="text-black text-sm hover:underline">
                  <strong>{translate(translations.readMore)} <RightOutlined /></strong>
                </span>
              </Card>
            </Link>
          )) : <EmptyImage />}
        </div>

        <Pagination
          className="my-10 text-center"
          current={currentPage}
          pageSize={pageSize}
          total={totalItems}
          onChange={page => {
            setCurrentPage(page)
            scrollUp()
          }}
        />


      </div>
    </div>
    <Footer />
  </div >

}
